import React, { useCallback, useMemo, useState } from "react";
import { Dashboard, Help, Share } from "@mui/icons-material";
import { Box } from "@mui/material";

import analytics from "fond/analytics";
import { selectCurrentAccount } from "fond/api";
import { HUBSPOT_FORM_CONTACT_SALES, HUBSPOT_FORM_PROVIDE_FEEDBACK } from "fond/constants";
import { useAccountSwitching } from "fond/hooks/useAccountSwitching";
import { useContents } from "fond/hooks/useContents";
import { useEntityUtil } from "fond/hooks/useEntityUtil";
import { useRecentItems } from "fond/hooks/useRecentItems";
import { useRoleCheck } from "fond/hooks/useRoleCheck";
import { useSortedAccounts } from "fond/hooks/useSortedAccounts";
import { useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import { HubSpotModal } from "fond/hubspot/HubSpotModal";
import { NoContentIcon } from "fond/svg_icons";
import { HubSpotFormFieldValues } from "fond/types/hubspot";
import { useAppSelector } from "fond/utils/hooks";
import NewItemButton from "fond/views/MainWorkspace/NewItemButton";
import InviteMembersButton from "fond/views/Settings/UserManagement/InviteMembersButton";
import { Avatar, NonIdealState } from "fond/widgets";
import { GuestChip, MemberChip } from "fond/widgets/AccountSwitch";

import { NavigationLinkMenu, NavigationLinkMenuItem } from "./NavigationLinkMenu";
import { SidebarTips } from "./SidebarTips";

import { HelpButton, NewNavigationContainer, SidebarElement, StyledNavLink, SupportNavigationItem, SupportNavigationMenu } from "./Navigation.styles";

const CONTAINER_MAX_WIDTH = 300;

const MAX_ITEMS = 4;

type FormDisplayState = {
  id?: string;
  title: string;
  initialData?: HubSpotFormFieldValues;
};

export const ProjectsNavigation: React.FC = () => {
  const [formDisplayState, setFormDisplayState] = useState<FormDisplayState | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { starredItems: allStartedItems, items: allItems, isLoading } = useContents();
  const allRecentItems = useRecentItems();
  const { getEntityIcon, getEntityUrl, getEntityName } = useEntityUtil();
  const shouldShowInviteButton = useRoleCheck("admin");
  const currentUsername = useAppSelector((state) => state.cognito.user?.username);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const filter = useAppSelector((state) => state.projects.filters);
  const accountName = currentAccount?.Name;
  const hasContent = allItems.length > 0;

  const helpMenuOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const showContactSalesForm = () => {
    setFormDisplayState({
      id: HUBSPOT_FORM_CONTACT_SALES,
      title: "Contact sales",
      initialData: {
        "0-1/email": currentUsername,
        "0-1/fond_accounts_name": accountName || "",
      },
    });
    analytics.track("sidebar.support.contact-sales.clicked");
    handleClose();
  };

  const showFeedbackForm = () => {
    setFormDisplayState({
      id: HUBSPOT_FORM_PROVIDE_FEEDBACK,
      title: "Provide feedback",
      initialData: {
        "0-1/email": currentUsername,
      },
    });
    analytics.track("sidebar.support.provide-feedback.clicked");
    handleClose();
  };

  const handleCloseHubSpotForm = () => {
    setFormDisplayState(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { accounts: allSwitchAccounts } = useSwitchableAccounts();
  const displayAccounts = useSortedAccounts(allSwitchAccounts);
  const { initiateSwitch, modal: accountSwitchingModal } = useAccountSwitching();

  const handleAccountSwitch = useCallback(
    (accountId: string, name: string) => (event: React.MouseEvent) => {
      event.preventDefault();
      initiateSwitch(accountId, name);
    },
    [initiateSwitch]
  );

  const starredItems = useMemo(
    () =>
      allStartedItems.slice(0, MAX_ITEMS).map((item) => ({
        ID: item.ID,
        entityType: item.EntityType,
        label: getEntityName(item),
        icon: getEntityIcon(item),
        url: getEntityUrl(item),
      })),
    [allStartedItems, getEntityIcon, getEntityName, getEntityUrl]
  );

  const recentItems: NavigationLinkMenuItem[] = useMemo(
    () =>
      allRecentItems.slice(0, MAX_ITEMS).map((item) => ({
        ID: item.ID,
        entityType: item.EntityType,
        label: getEntityName(item),
        icon: getEntityIcon(item),
        url: getEntityUrl(item),
      })),
    [allRecentItems, getEntityIcon, getEntityName, getEntityUrl]
  );

  const myAccounts = displayAccounts.map((account) => ({
    ID: account.ID,
    label: account.Name,
    icon: <Avatar name={account.Name} variant="rounded" sx={{ width: 32, height: 32 }} stringColor maxInitials={3} />,
    url: "#", // Placeholder URL since we handle click with custom handler
    onClick: handleAccountSwitch(account.ID, account.Name),
    rightAdornment: account.Guest ? (
      <GuestChip label="Guest" size="small" variant="outlined" />
    ) : (
      <MemberChip label="Member" size="small" variant="outlined" />
    ),
  }));

  return (
    <>
      <NewNavigationContainer display="flex" flexDirection="column" testId="project-navigation">
        <Box maxWidth={CONTAINER_MAX_WIDTH} display="flex" flexDirection="column" height="100%" className="navigation-container">
          <Box className="primary-actions">
            {!hasContent && !currentAccount && !isLoading && (
              <SidebarElement>
                <NonIdealState
                  description="It looks like you are not part of an account and have not been shared content."
                  size="small"
                  title="No content"
                  icon={<NoContentIcon />}
                />
              </SidebarElement>
            )}
            {shouldShowInviteButton && (
              <SidebarElement>
                <InviteMembersButton variant="outlined" fullWidth />
              </SidebarElement>
            )}
            <SidebarElement>
              <NewItemButton width="100%" source="sidebar">
                Create
              </NewItemButton>
            </SidebarElement>

            <SidebarElement>
              <SidebarTips />
            </SidebarElement>
          </Box>
          <Box className="secondary-actions" mt={3}>
            {currentAccount && (
              <>
                <Box mb={2}>
                  <StyledNavLink to="/workspace" className={filter === "workspace" ? "active" : ""}>
                    <span className="icon">
                      <Dashboard />
                    </span>
                    Main Workspace
                  </StyledNavLink>
                </Box>
                <Box mb={2}>
                  <StyledNavLink to="/shared" className={filter === "shared" ? "active" : ""}>
                    <span className="icon">
                      <Share />
                    </span>
                    Shared with me
                  </StyledNavLink>
                </Box>
              </>
            )}
            {myAccounts.length > 0 && (
              <NavigationLinkMenu label="My accounts" emptyMessage="No accounts" to="/accounts" items={myAccounts} id="shared" seeAllText="Manage" />
            )}
            {hasContent && (
              <>
                <NavigationLinkMenu label="Starred" emptyMessage="No starred items" to="/starred" items={starredItems} id="starred" />
                <NavigationLinkMenu label="Recent" emptyMessage="No recent items" to="/recent" items={recentItems} id="recent" />
              </>
            )}
          </Box>

          <Box className="teriary-actions" mb={3} mt="auto">
            <SidebarElement>
              <HelpButton
                variant="text"
                color="inherit"
                id="fond-sidebar-help-button"
                aria-controls={helpMenuOpen ? "fond-sidebar-help-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={helpMenuOpen ? "true" : undefined}
                onClick={handleClick}
                fullWidth
              >
                <Help color="primary" /> Help
              </HelpButton>
              <SupportNavigationMenu
                id="fond-sidebar-help-menu"
                anchorEl={anchorEl}
                open={helpMenuOpen}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                slotProps={{
                  root: { "aria-labelledby": "fond-sidebar-help-button" },
                }}
                MenuListProps={{ component: "nav" }}
                elevation={1}
              >
                <SupportNavigationItem
                  onClick={() => analytics.track("sidebar.support.knowledge-base.clicked")}
                  to="https://fondhelp.biarrinetworks.com/"
                >
                  Knowledge base
                </SupportNavigationItem>
                <SupportNavigationItem
                  onClick={() => analytics.track("sidebar.support.contact-support.clicked")}
                  to="mailto:fondhelp@biarrinetworks.com"
                >
                  Contact support
                </SupportNavigationItem>
                <SupportNavigationItem onClick={showContactSalesForm}>Contact sales</SupportNavigationItem>
                <SupportNavigationItem onClick={showFeedbackForm}>Submit feedback</SupportNavigationItem>
              </SupportNavigationMenu>
            </SidebarElement>
          </Box>
        </Box>
        {formDisplayState !== null && (
          <HubSpotModal
            header={formDisplayState.title}
            formId={formDisplayState.id}
            initialData={formDisplayState.initialData}
            onClose={handleCloseHubSpotForm}
          />
        )}
      </NewNavigationContainer>
      {accountSwitchingModal}
    </>
  );
};

export default React.memo(ProjectsNavigation);
